"use client";

import { IEventTyCard, PageEventThankyouSchema } from "@/sanity/pages/PageEventThankyouTemplate";
import NextImage from "next/image";
import { stegaClean } from "@sanity/client/stega";

import {
  BlackCTA,
  CAP_TRIM,
  CountdownBox,
  EventFooter,
  EventHeroBackdrop,
  EventNavbar,
  TicketCTA,
  ticketPieceMask,
  wave,
} from "@/components/pageComponents/event/EventShared";
import columns from "@/public/event/columns.png";
import iconScan from "@/public/event/icon-scan.svg";
import zap from "@/public/event/zapzap.png";
import { MonthlyCardMedia } from "@/components/pageComponents/monthly-update/MonthlyCardMedia";
import Image from "next/image";
import { cn } from "@/lib/utils";

/** Single-label ticket tab above thank-you cards (torn right edge). */
const TY_TAB_MASK = ticketPieceMask(["right"], {
  notchR: "0.95rem",
  notchOffset: "0.4rem",
  outline: { band: "0.33rem", line: "0.12rem" },
  dashes: { width: "0.12rem", length: "0.22rem", period: "0.44rem", top: "0.95rem", height: "4.75rem" },
});

/** Single detached ticket-piece label above a card. */
function TyCardTab({ label, index }: { label?: string; index: number }) {
  if (!label) return null;
  return (
    <span
      style={TY_TAB_MASK}
      className="flex h-[5.5rem] w-fit items-center bg-[#ffda74] px-[2.5rem] pr-[3.5rem] text-black">
      <span className="font-geist text-[1.75rem] leading-[1.9rem] font-medium whitespace-nowrap uppercase">
        {`${index + 1}. ${label}`}
      </span>
    </span>
  );
}

function TyContentCard({ card, index, language, matchFigma }: { card: IEventTyCard; index: number; language: string; matchFigma: boolean }) {
  return (
    <div className={cn("flex w-full flex-col gap-[4rem] border-[0.112rem] border-black/10 bg-white p-[2rem] lg:w-[46.67rem]", matchFigma && "max-lg:p-[2.5rem] lg:gap-[3rem]")}>
      {/* Always the monthly-update animations: uploaded visual/video are ignored on this page. */}
      <MonthlyCardMedia
        card={{ ...card, visual: undefined, visualVideo: undefined }}
        index={index}
        language={language}
      />
      <div className={cn("flex flex-col gap-[4rem]", matchFigma && "max-lg:order-first lg:gap-[2.75rem]", matchFigma && (index === 2 ? "lg:pb-[1rem]" : "lg:px-[1.25rem] lg:pb-[1.5rem]"))}>
        <TyCardTab
          label={card.tabLabel}
          index={index}
        />
        <div className="flex flex-col gap-[2.5rem]">
          {card.title && (
            <p className={cn("mb-0 w-[28.5rem] font-geist text-[3rem] leading-[1.1] font-normal tracking-[-0.09rem] text-black", matchFigma && CAP_TRIM)}>
              {card.title}
            </p>
          )}
          {(card.description || card.descriptionStrong) && (
            <p className={cn("mb-0 w-full font-geist text-[2rem] leading-[1.5] font-normal tracking-[-0.06rem] whitespace-pre-line", matchFigma && CAP_TRIM)}>
              {card.description && <span className="text-black/70">{card.description} </span>}
              {card.descriptionStrong && <span className="text-black/70">{card.descriptionStrong}</span>}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PageEventThankyouJSX({ data }: { data: PageEventThankyouSchema }) {
  const infoItems = data.infoItems ?? [];
  const cards = data.s2Cards ?? [];
  const footerLinks = data.footerLinks ?? [];
  // The DE doc has no settings.language, so fall back to the /de/ slug prefix.
  const language = stegaClean(
    data.settings.language ?? (stegaClean(data.settings.slug?.current ?? "").startsWith("/de/") ? "de" : "en"),
  );
  const isGermanMonthlyThankyou = language === "de" && stegaClean(data.settings.slug?.current ?? "") === "/de/monthly-thankyou";

  return (
    <div className={cn("relative w-full bg-[#10322a]", isGermanMonthlyThankyou && "[--page-rem:1rem]")}>
      <section className={cn("relative w-full overflow-clip rounded-b-[1.25rem] bg-[#10322a] pb-[8rem] lg:h-[135rem] lg:min-h-0 lg:pb-0", isGermanMonthlyThankyou && "pb-[8.875rem] lg:h-auto lg:min-h-[119.875rem] lg:pb-[11.375rem]")}>
        <EventHeroBackdrop
          candlePositions={["left-[-3.5rem] top-[75.625rem]", "right-[-8.875rem] top-[32rem] lg:block hidden"]}
        />

        <EventNavbar
          data={data.navCta}
          textMobile={data.navCtaTextMobile}
          countdownTarget={data.countdownTarget}
          language={language}
        />

        {/* The German mobile title sits 39px below the compact countdown at the 430px reference. */}
        <div className={cn("relative z-10 mx-auto flex w-[49.75rem] flex-col items-center gap-[4rem] pt-[20rem] lg:w-[139.5rem] lg:pt-[22rem]", isGermanMonthlyThankyou && "pt-[20.427rem] lg:pt-[15.75rem]")}>
          <div className="flex w-full flex-col items-center gap-[1.5rem]">
            {data.badgeText && (
              <div className={cn("hidden items-center gap-[1.25rem] border border-white/10 bg-white/[0.04] px-[2.5rem] py-[1.5rem] lg:flex", isGermanMonthlyThankyou && "py-[1.375rem]")}>
                <span className="h-[0.75rem] w-[0.75rem] rounded-full bg-[#1b8869]" />
                <p className={cn("mb-0 text-center font-geist text-[1.75rem] leading-[1.15] font-medium whitespace-nowrap text-white uppercase", isGermanMonthlyThankyou && CAP_TRIM)}>
                  {data.badgeText}
                </p>
              </div>
            )}
            <div className={cn("flex flex-col items-center justify-center", isGermanMonthlyThankyou && "gap-[4rem] lg:gap-[3.5rem]")}>
              {(data.titleRegular || data.titleItalic) && (
                <h1 className={cn("m0-0 w-full text-center font-blacker text-[4.625rem] leading-[1.12] font-normal tracking-[-0.14rem] text-white lg:w-[81.375rem] lg:text-[7rem] lg:tracking-[-0.21rem] [&_span]:font-blacker", isGermanMonthlyThankyou && "lg:mt-[1.125rem] lg:[text-box-trim:trim-both] lg:[text-box-edge:cap_alphabetic]")}>
                  {data.titleRegular && <span>{data.titleRegular} </span>}
                  {data.titleItalic && <span className="italic">{data.titleItalic}</span>}
                </h1>
              )}
              {data.description && (
                <p className={cn("mb-0 w-full px-[2rem] text-center font-geist text-[2.25rem] leading-[1.6] font-normal text-white opacity-60 lg:w-[72.375rem] lg:px-0", isGermanMonthlyThankyou && CAP_TRIM)}>
                  {data.description}
                </p>
              )}
            </div>
          </div>

          <TicketCTA
            data={data.cta}
            className={cn("w-auto min-w-[36.575rem]", isGermanMonthlyThankyou && "max-lg:w-full")}
          />

          <div className={cn("flex w-full flex-col items-start justify-center gap-[0.5rem] lg:w-[72rem]", isGermanMonthlyThankyou && "lg:mt-[1rem]")}>
            {/* 2×2 info cards */}
            {!!infoItems.length && (
              <div className="grid w-full grid-cols-2 gap-[0.5rem]">
                {infoItems.slice(0, 4).map((item, i) => (
                  <div
                    key={item._key ?? i}
                    className="flex min-w-0 items-center gap-[1.375rem] rounded-[0.35rem] bg-white p-[1.375rem] lg:gap-[2rem] lg:rounded-[0.5rem] lg:p-[2rem]">
                    <span className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-[0.8rem] bg-[#ffda74] lg:h-[6.125rem] lg:w-[6.125rem] lg:rounded-[1.16rem]">
                      {item.icon?.src && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={item.icon.src}
                          alt=""
                          className="h-[2.1rem] w-[2.1rem] lg:h-[3rem] lg:w-[3rem]"
                        />
                      )}
                    </span>
                    <span className="flex min-w-0 flex-col gap-[0.35rem] leading-[1.35] text-black lg:gap-[0.5rem]">
                      <span className="truncate font-geist text-[2rem] font-medium lg:text-[2.5rem]">{item.value}</span>
                      <span className="truncate font-geist text-[1.75rem] font-normal opacity-80 lg:text-[2rem]">
                        {item.label}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}

            <CountdownBox
              label={data.countdownLabel}
              target={data.countdownTarget}
              language={language}
              variant="light"
              layout={isGermanMonthlyThankyou ? "thankyou" : "event"}
              className="lg:w-full"
            />
          </div>
        </div>
      </section>

      {/* ── Event contents section ─────────────────────────────────────── */}
      <section className={cn("flex w-full flex-col items-center gap-[15rem] bg-white px-[2rem] py-[8rem] lg:py-[15rem]", isGermanMonthlyThankyou && "max-lg:gap-[8rem] max-lg:pb-0")}>
        <div className={cn("flex w-full flex-col items-center gap-[4rem]", isGermanMonthlyThankyou && "gap-[3rem] lg:max-w-[150rem] lg:gap-[4rem]")}>
          <div className="flex w-full flex-col items-center gap-[2.75rem]">
            {data.s2BadgeText && (
              <div className={cn("flex items-center gap-[1.25rem] border border-black/10 bg-black/[0.04] px-[2.5rem] py-[1.5rem]", isGermanMonthlyThankyou && "py-[1.375rem]")}>
                <span className="h-[0.75rem] w-[0.75rem] rounded-full bg-[#1b8869]" />
                <p className={cn("mb-0 font-geist text-[1.75rem] leading-[1.15] font-medium whitespace-nowrap text-black uppercase", isGermanMonthlyThankyou && CAP_TRIM)}>
                  {data.s2BadgeText}
                </p>
              </div>
            )}
            {(data.s2TitleAccent || data.s2TitleRest) && (
              <h2 className={cn("mb-0 w-[41rem] text-center font-blacker text-[4.5rem] leading-[1.12] font-normal tracking-[-0.135rem] text-black lg:w-[74.75rem] lg:text-[6rem] lg:tracking-[-0.18rem] [&_span]:font-blacker", isGermanMonthlyThankyou && CAP_TRIM)}>
                {data.s2TitleAccent && <span className="text-[#296e5a]">{data.s2TitleAccent} </span>}
                {data.s2TitleRest}
              </h2>
            )}
            {(data.s2DescriptionMuted || data.s2DescriptionStrong) && (
              <p className={cn("mb-0 w-[45.75rem] text-center font-geist text-[2.25rem] leading-[1.5] font-normal lg:w-[63.875rem]", isGermanMonthlyThankyou && CAP_TRIM)}>
                {data.s2DescriptionMuted && <span className="text-black/70">{data.s2DescriptionMuted} </span>}
                {data.s2DescriptionStrong && <span className="text-black">{data.s2DescriptionStrong}</span>}
              </p>
            )}
          </div>

          {!!cards.length && (
            <div className={cn("flex w-full flex-col items-stretch justify-center gap-[3rem] lg:flex-row lg:gap-[5rem]", isGermanMonthlyThankyou && "gap-[5rem]")}>
              {cards.slice(0, 3).map((card, i) => (
                <TyContentCard
                  key={card._key ?? i}
                  card={card}
                  index={i}
                  language={language}
                  matchFigma={isGermanMonthlyThankyou}
                />
              ))}
            </div>
          )}
        </div>

        {/* WhatsApp banner */}
        {(data.waTitleItalic || data.waTitleRegular) && (
          <div className={cn("relative flex h-[94rem] w-full flex-col overflow-clip rounded-[0.5rem] border border-black/5 bg-[#f9f9f9] px-[2rem] py-[6rem] lg:h-[56.125rem] lg:w-[150rem] lg:flex-row lg:items-center lg:justify-between lg:bg-white lg:px-[5rem] lg:py-[2.75rem]", isGermanMonthlyThankyou && "max-lg:w-[calc(100%+4rem)] max-lg:rounded-none max-lg:border-0")}>
            <div className="relative z-10 flex w-full flex-col items-center justify-center gap-[2rem] lg:w-auto lg:items-start">
              <div className="flex flex-col rounded-[0.25rem] border border-[#001b14]/10 p-[0.75rem]">
                <span className="relative h-[5.75rem] w-[5.75rem] rounded-[0.25rem] bg-[#001b14]/5 lg:bg-[#1aa87f]/10">
                  <NextImage
                    src={iconScan}
                    alt=""
                    aria-hidden
                    className="absolute top-1/2 left-1/2 h-[3rem] w-[3rem] -translate-x-1/2 -translate-y-1/2"
                  />
                </span>
              </div>
              <h2 className={cn("mb-0 w-[31rem] text-center font-geist text-[4rem] leading-[1.15] font-normal tracking-[-0.2rem] text-black lg:w-[46.5rem] lg:text-left lg:font-blacker lg:text-[6rem] lg:tracking-[-0.3rem] lg:[&_span]:font-blacker", isGermanMonthlyThankyou && "max-lg:[text-box-trim:trim-both] max-lg:[text-box-edge:cap_alphabetic]")}>
                {data.waTitleItalic && <span className="lg:italic">{data.waTitleItalic} </span>}
                {data.waTitleRegular && (
                  <span className="font-blacker text-[#003828] italic lg:text-black lg:not-italic">
                    {data.waTitleRegular}
                  </span>
                )}
              </h2>
              {data.waDescription && (
                <p className="mb-0 w-[41.5rem] text-center font-geist text-[2.25rem] leading-[1.5] font-normal tracking-[-0.1125rem] text-black opacity-60 lg:w-[53.5rem] lg:text-left lg:text-[2.75rem] lg:tracking-[-0.1375rem]">
                  {data.waDescription}
                </p>
              )}
              {/* Mobile: yellow ticket CTA (Figma). Desktop keeps the black button. */}
              <TicketCTA
                data={data.waButton}
                className="w-full lg:hidden"
              />
              <BlackCTA
                data={data.waButton}
                className={cn("mt-[1rem] hidden lg:flex", isGermanMonthlyThankyou && "mt-0")}
              />
            </div>
            {data.waImage?.src && (
              // eslint-disable-next-line @next/next/no-img-element
              <Image
                src={zap}
                alt=""
                className="ax-w-none absolute bottom-0 left-1/2 w-[min(56rem,100%)] -translate-x-1/2 mix-blend-multiply lg:top-0 lg:right-0 lg:bottom-auto lg:left-auto lg:translate-x-0 lg:object-cover lg:mix-blend-normal"
              />
            )}
          </div>
        )}
      </section>

      {/* ── Final CTA + footer ─────────────────────────────────────────── */}
      <section className={cn("relative flex w-full flex-col items-center justify-center overflow-clip rounded-t-[1.25rem] bg-[#19362e] px-[2rem] pt-[9rem] pb-[26rem] lg:h-[97.25rem] lg:px-0 lg:pt-0 lg:pb-[10rem]", isGermanMonthlyThankyou && "justify-start px-0 pt-[8.8265rem] pb-0 lg:h-auto lg:min-h-[94.125rem] lg:pt-[16.25rem] lg:pb-0")}>
        <div
          aria-hidden
          className="pointer-events-none absolute top-[60rem] left-[-4.375rem] h-[156.25rem] w-[271.875rem] lg:top-[64.4rem]">
          <div className="absolute inset-[-47.12%_-27.07%]">
            <NextImage
              src={wave}
              alt=""
              className="block !h-full !w-full !max-w-none"
            />
          </div>
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute top-[2rem] left-[-71.4rem] hidden h-[131.2rem] w-[168.125rem] lg:block">
          <NextImage
            src={columns}
            alt=""
            className="absolute inset-0 !h-full !w-full !max-w-none object-cover"
          />
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute top-[2rem] right-[-71.4rem] hidden h-[131.2rem] w-[168.125rem] -scale-x-100 lg:block">
          <NextImage
            src={columns}
            alt=""
            className="absolute inset-0 !h-full !w-full !max-w-none object-cover"
          />
        </div>

        <div className={cn("relative z-10 flex w-full flex-col items-center gap-[4rem] px-[2rem] lg:w-[84.5rem]", isGermanMonthlyThankyou && "gap-0 lg:px-0")}>
          {data.fTitleItalic && (
            <h2 className={cn("mb-0 text-center font-blacker text-[4.5rem] leading-[1.12] font-normal tracking-[-0.135rem] text-white italic lg:text-[6rem] lg:tracking-[-0.18rem] lg:whitespace-nowrap [&_span]:font-blacker", isGermanMonthlyThankyou && `${CAP_TRIM} mb-[3rem] lg:w-full lg:whitespace-normal`)}>
              {data.fTitleItalic}
            </h2>
          )}
          {(data.fDescriptionStrong || data.fDescriptionMuted) && (
            <p className={cn("mb-0 w-full text-center font-geist text-[2.25rem] leading-[1.5] font-normal lg:w-[67.625rem]", isGermanMonthlyThankyou && `${CAP_TRIM} mb-[4.9096rem] lg:mb-[4rem]`)}>
              {data.fDescriptionStrong && <span className="text-white">{data.fDescriptionStrong} </span>}
              {data.fDescriptionMuted && <span className="text-white/70">{data.fDescriptionMuted}</span>}
            </p>
          )}
          <TicketCTA
            data={data.fCta}
            className={cn("w-auto min-w-[39.45rem]", isGermanMonthlyThankyou && "mb-[1.6596rem] max-lg:w-[46.9896rem] max-lg:max-w-full lg:mb-[4rem]")}
          />
          <CountdownBox
            label={data.countdownLabel}
            target={data.countdownTarget}
            language={language}
            variant="light"
            layout={isGermanMonthlyThankyou ? "monthly" : "event"}
            fitWidth={isGermanMonthlyThankyou}
            className={isGermanMonthlyThankyou ? "max-lg:w-[46.886rem] max-lg:max-w-full" : undefined}
          />
        </div>

        <EventFooter
          copyright={data.footerCopyright}
          links={footerLinks}
          className={isGermanMonthlyThankyou ? "relative mt-[10.4417rem] lg:mt-[19.275rem]" : undefined}
          linksClassName={isGermanMonthlyThankyou ? "max-lg:text-[1.625rem]" : undefined}
        />
      </section>
    </div>
  );
}
